/*  MUI  */
import { Grid } from '@mui/material';

/*  Shared  */
import { BreakdownCard } from 'application/Shared/Components/Cards/Components/BreakdownCard';
import { useManagedTableV2Context } from 'application/Shared/Components/Table/ManagedTableV2ContextProvider';
import { Helpers } from 'application/Shared/Helpers';
import { TenantLicenseUsage } from 'application/Shared/types/tenantLicenseUsage';

/*  React  */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    LicenseCardData,
    LicenseTypesFlyoutMenu
} from './LicenseTypesFlyoutMenu';

const MAX_LICENSE_TYPES = 5;

type DataType = 'unused' | 'lastLoginTimeOver30daysAgo' | 'total';

type FlyoutState = {
    title: string;
    dataType: DataType | null;
    useFilteredData: boolean;
};

export const IntegrationAppUserDataCards = () => {
    const { t } = useTranslation();
    const [licenseTypes, setLicenseTypes] = useState<string[]>([]);
    const [currentFilter, setCurrentFilter] = useState<DataType | null>(null);
    const [isFlyoutOpen, setIsFlyoutOpen] = useState(false);
    const [flyoutState, setFlyoutState] = useState<FlyoutState>({
        dataType: null,
        title: '',
        useFilteredData: false
    });

    const {
        data: rowData,
        table,
        toggles
    } = useManagedTableV2Context<TenantLicenseUsage>();

    // Create a constant to store the disabled users toggle state
    const excludeDisabledUsersToggleState =
        toggles.includeUser?.enabled ?? false;

    const data = table.getFilteredRowModel().rows.map((d) => d.original);
    const { columnFilters } = table.getState();
    const filteredResults = rowData?.length !== data.length;

    // Store an updated list of all the different license types
    useEffect(() => {
        if (rowData) {
            const licenseTypeSet = new Set(
                rowData.map((d) => d['licenseTier+skuName'])
            );
            setLicenseTypes(Array.from(licenseTypeSet) as string[]);
        }
    }, [rowData]);

    const getFilterById = useCallback(
        (id: string) => {
            const columnFilter = columnFilters?.find(
                (filter) => filter.id === id
            );
            return {
                id,
                value: (columnFilter?.value ?? []) as string[]
            };
        },
        [columnFilters]
    );

    const applyDataTypeFilter = useCallback(
        (dataType: DataType, licenseType: string) => {
            const filterConfig = {
                lastLoginTimeOver30daysAgo: {
                    id: 'lastLoginTimeOver30daysAgo',
                    value: ['over30days']
                },
                unused: { id: 'userCount', value: ['noUsers'] }
            };
            const currentFilterId =
                filterConfig[dataType as keyof typeof filterConfig]?.id;
            const filterIds = ['licenseType', currentFilterId];
            const otherFilters =
                columnFilters?.filter(
                    (filter) => !filterIds.includes(filter.id)
                ) || [];
            const newFilters = [
                { id: 'licenseType', value: [licenseType] },
                filterConfig[dataType as keyof typeof filterConfig]
            ].filter(Boolean);
            table.setColumnFilters([...otherFilters, ...newFilters]);
        },
        [columnFilters, table]
    );

    const clearFilters = useCallback(() => {
        const filterIds = [
            'licenseType',
            'userCount',
            'lastLoginTimeOver30daysAgo'
        ];
        const otherFilters =
            columnFilters?.filter((filter) => !filterIds.includes(filter.id)) ||
            [];
        table.setColumnFilters([
            ...otherFilters,
            { id: 'licenseType', value: [] },
            { id: 'userCount', value: [] },
            { id: 'lastLoginTimeOver30daysAgo', value: [] }
        ]);
    }, [columnFilters, table]);

    const applyTotalFilter = useCallback(
        (licenseType: string) => {
            table.setColumnFilters([
                { id: 'licenseType', value: [licenseType] }
            ]);
        },
        [table]
    );

    const updateFilters = useCallback(
        (licenseType: string, dataType: DataType) => {
            const licenseFilter = getFilterById('licenseType');
            const isAlreadyFiltered =
                currentFilter === dataType &&
                licenseFilter.value.includes(licenseType);

            if (isAlreadyFiltered) {
                clearFilters();
            } else if (dataType === 'total') {
                applyTotalFilter(licenseType);
            } else {
                applyDataTypeFilter(dataType, licenseType);
            }

            setCurrentFilter(isAlreadyFiltered ? null : dataType);
        },
        [
            applyDataTypeFilter,
            applyTotalFilter,
            clearFilters,
            currentFilter,
            getFilterById
        ]
    );

    const isUnusedLicense = useCallback(
        (license: TenantLicenseUsage) =>
            !license.platformUserId ||
            (excludeDisabledUsersToggleState
                ? license.accounts.every((account) => account.disabled)
                : license.accounts.length === 0),
        [excludeDisabledUsersToggleState]
    );

    const isUnderutilizedLicense = useCallback(
        (license: TenantLicenseUsage) =>
            license.metadata.lastLoginTime &&
            Helpers.isDateMoreThan30DaysAgo(
                license.metadata.lastLoginTime as string
            ),
        []
    );

    const isTotalLicenseCount = useCallback(
        (license: TenantLicenseUsage) => {
            const licenseFilter = getFilterById('licenseType');
            return (
                licenseFilter?.value.length === 0 ||
                licenseFilter?.value.includes(license['licenseTier+skuName'])
            );
        },
        [getFilterById]
    );

    const shouldUpdateLicenseCounts = useCallback(
        (license: TenantLicenseUsage) => {
            const isUnused = isUnusedLicense(license);
            const isUnderutilized = isUnderutilizedLicense(license);
            const isTotal = isTotalLicenseCount(license);

            return {
                total: isTotal,
                underutilized: isUnderutilized,
                unused: isUnused
            };
        },
        [isTotalLicenseCount, isUnderutilizedLicense, isUnusedLicense]
    );

    const createLicenseCardData = useCallback(
        (
            dataSource: TenantLicenseUsage[] | undefined
        ): {
            totalAccountsData: LicenseCardData[];
            underUtilizedAccountsData: LicenseCardData[];
            unusedAccountsData: LicenseCardData[];
        } => {
            const cardData = {
                totalAccountsData: [] as LicenseCardData[],
                underUtilizedAccountsData: [] as LicenseCardData[],
                unusedAccountsData: [] as LicenseCardData[]
            };

            if (!dataSource?.length || !licenseTypes.length) return cardData;

            const licenseCounts = dataSource.reduce<{
                unused: Record<string, number>;
                underutilized: Record<string, number>;
                total: Record<string, number>;
            }>(
                (acc, license) => {
                    const licenseKey =
                        license['licenseTier+skuName'] || t('Common.none');
                    const counts = shouldUpdateLicenseCounts(license);

                    if (counts.unused) {
                        acc.unused[licenseKey] =
                            (acc.unused[licenseKey] || 0) + 1;
                    }
                    if (counts.underutilized) {
                        acc.underutilized[licenseKey] =
                            (acc.underutilized[licenseKey] || 0) + 1;
                    }
                    if (counts.total) {
                        acc.total[licenseKey] =
                            (acc.total[licenseKey] || 0) + 1;
                    }
                    return acc;
                },
                { total: {}, underutilized: {}, unused: {} }
            );

            licenseTypes.forEach((licenseType) => {
                const baseCard = {
                    label: licenseType || t('Common.none'),
                    value: 0
                };

                cardData.unusedAccountsData.push({
                    ...baseCard,
                    id: `unused_${licenseType.toLowerCase()}`,
                    onClick: () => updateFilters(licenseType, 'unused'),
                    value: licenseCounts.unused[licenseType] || 0
                });

                cardData.underUtilizedAccountsData.push({
                    ...baseCard,
                    id: `underutilized_${licenseType.toLowerCase()}`,
                    onClick: () =>
                        updateFilters(
                            licenseType,
                            'lastLoginTimeOver30daysAgo'
                        ),
                    value: licenseCounts.underutilized[licenseType] || 0
                });

                cardData.totalAccountsData.push({
                    ...baseCard,
                    id: `total_${licenseType.toLowerCase()}`,
                    onClick: () => updateFilters(licenseType, 'total'),
                    value: licenseCounts.total[licenseType] || 0
                });
            });

            // Sort all arrays
            Object.keys(cardData).forEach((key) => {
                cardData[key as keyof typeof cardData].sort(
                    (a, b) =>
                        b.value - a.value || a.label.localeCompare(b.label)
                );
            });

            return cardData;
        },
        [licenseTypes, t, updateFilters, shouldUpdateLicenseCounts]
    );

    // Memoize card data
    const { unusedAccountsData, underUtilizedAccountsData } = useMemo(
        () => createLicenseCardData(data),
        [createLicenseCardData, data]
    );

    const {
        totalAccountsData,
        unusedAccountsData: totalUnusedAccountsData,
        underUtilizedAccountsData: totalUnderUtilizedAccountsData
    } = useMemo(
        () => createLicenseCardData(rowData),
        [createLicenseCardData, rowData]
    );

    // Generate flyout data based on current state
    const dynamicFlyoutData = useMemo(() => {
        if (!flyoutState.dataType) return [];

        if (flyoutState.useFilteredData) {
            if (flyoutState.dataType === 'unused') {
                return unusedAccountsData;
            }
            return underUtilizedAccountsData;
        }

        if (flyoutState.dataType === 'unused') {
            return totalUnusedAccountsData;
        }

        if (flyoutState.dataType === 'total') {
            return totalAccountsData;
        }
        return totalUnderUtilizedAccountsData;
    }, [
        flyoutState.dataType,
        flyoutState.useFilteredData,
        unusedAccountsData,
        underUtilizedAccountsData,
        totalUnusedAccountsData,
        totalUnderUtilizedAccountsData,
        totalAccountsData
    ]);

    const showFlyout = useCallback(
        ({
            dataType,
            title,
            useFiltered
        }: {
            dataType: DataType;
            title: string;
            useFiltered: boolean;
        }) => {
            setIsFlyoutOpen(true);
            setFlyoutState({
                dataType,
                title,
                useFilteredData: useFiltered
            });
        },
        []
    );

    return (
        <Grid container spacing={3} alignItems="stretch">
            <Grid item xs={12} md={4}>
                <BreakdownCard
                    data={totalAccountsData}
                    filteredResults={filteredResults}
                    maxTypes={MAX_LICENSE_TYPES}
                    onClickSeeAll={() =>
                        showFlyout({
                            dataType: 'total',
                            title: t('Common.totalLicenses'),
                            useFiltered: false
                        })
                    }
                    seeAllLabel={t('LicenseData.card.seeAll')}
                    title={t('Common.totalLicenses')}
                />
            </Grid>
            <Grid item xs={12} md={4}>
                <BreakdownCard
                    data={unusedAccountsData}
                    filteredResults={filteredResults}
                    maxTypes={MAX_LICENSE_TYPES}
                    onClickSeeAll={() =>
                        showFlyout({
                            dataType: 'unused',
                            title: t(
                                'SaasOps.licenses.totalLicensesWithUndetectedAccounts'
                            ),
                            useFiltered: false
                        })
                    }
                    seeAllLabel={t('LicenseData.card.seeAll')}
                    title={t(
                        'SaasOps.licenses.totalLicensesWithUndetectedAccounts'
                    )}
                    tooltip={t('License.UndetectedAccounts')}
                />
            </Grid>
            <Grid item xs={12} md={4}>
                <BreakdownCard
                    data={underUtilizedAccountsData}
                    filteredResults={filteredResults}
                    maxTypes={MAX_LICENSE_TYPES}
                    onClickSeeAll={() =>
                        showFlyout({
                            dataType: 'lastLoginTimeOver30daysAgo',
                            title: t(
                                'SaasOps.licenses.totalUnderutilizedLicenses'
                            ),
                            useFiltered: false
                        })
                    }
                    seeAllLabel={t('LicenseData.card.seeAll')}
                    title={t('SaasOps.licenses.totalUnderutilizedLicenses')}
                    tooltip={t('License.TotalNumberOfUnderutilizedLicenses')}
                />
            </Grid>
            {dynamicFlyoutData.length > MAX_LICENSE_TYPES && isFlyoutOpen && (
                <LicenseTypesFlyoutMenu
                    data={dynamicFlyoutData}
                    onClose={() => setIsFlyoutOpen(false)}
                    title={flyoutState.title}
                />
            )}
        </Grid>
    );
};
